import React, { useEffect, useState } from 'react';
import { SearchIcon, Loader2Icon, AlertCircleIcon, ArrowUpDownIcon } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { ProfitabilityItem } from './types';
interface Props {
  restaurantId: string;
}
type SortField = 'item_name' | 'gross_profit' | 'gross_margin_pct' | 'max_units_possible';
type SortOrder = 'asc' | 'desc';
export function ProfitabilityTable({
  restaurantId
}: Props) {
  const [items, setItems] = useState<ProfitabilityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [sortField, setSortField] = useState<SortField>('gross_profit');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  useEffect(() => {
    async function fetchProfitability() {
      try {
        setLoading(true);
        const {
          data,
          error
        } = await supabase.from('v_menu_profitability').select('*').eq('restaurant_id', restaurantId);
        if (error) throw error;
        setItems(data || []);
      } catch (err: any) {
        console.error('Error fetching profitability:', err);
        setError(err.message || 'Error al cargar rentabilidad');
      } finally {
        setLoading(false);
      }
    }
    fetchProfitability();
  }, [restaurantId]);
  const categories = ['All', ...Array.from(new Set(items.map(item => item.category)))];
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };
  const filteredAndSortedItems = items.filter(item => categoryFilter === 'All' || item.category === categoryFilter).filter(item => item.item_name.toLowerCase().includes(search.toLowerCase())).sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    return sortOrder === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
  });
  if (error) {
    return <div className="p-6 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-center" data-id="element-317">
        <AlertCircleIcon className="w-8 h-8 mx-auto mb-2 opacity-80" data-id="element-318" />
        <p data-id="element-319">{error}</p>
      </div>;
  }
  return <div className="space-y-4" data-id="element-320">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-321">
        <div className="relative w-full sm:w-72" data-id="element-322">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" data-id="element-323" />
          <input type="text" placeholder="Buscar plato..." value={search} onChange={e => setSearch(e.target.value)} className="w-full bg-surface border border-gray-800 rounded-lg pl-9 pr-4 py-2 text-sm text-white focus:outline-none focus:border-dorado transition-colors" data-id="element-324" />
        </div>
        <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="bg-surface border border-gray-800 rounded-lg px-4 py-2 text-sm text-white focus:outline-none focus:border-dorado transition-colors w-full sm:w-auto" data-id="element-325">
          {categories.map(cat => <option key={cat} value={cat} data-id="element-326">
              {cat === 'All' ? 'Todas las categorías' : cat}
            </option>)}
        </select>
      </div>

      {/* Table */}
      <div className="bg-surface border border-gray-800 rounded-xl overflow-hidden" data-id="element-327">
        <div className="overflow-x-auto" data-id="element-328">
          <table className="w-full text-left text-sm" data-id="element-329">
            <thead className="bg-surface-light text-gray-400 border-b border-gray-800" data-id="element-330">
              <tr data-id="element-331">
                <th className="px-4 py-3 font-medium cursor-pointer hover:text-white transition-colors" onClick={() => handleSort('item_name')} data-id="element-332">
                  <div className="flex items-center gap-1" data-id="element-333">
                    Plato <ArrowUpDownIcon className="w-3 h-3" data-id="element-334" />
                  </div>
                </th>
                <th className="px-4 py-3 font-medium" data-id="element-335">Categoría</th>
                <th className="px-4 py-3 font-medium text-right" data-id="element-336">
                  Precio Venta
                </th>
                <th className="px-4 py-3 font-medium text-right" data-id="element-337">
                  Costo Receta
                </th>
                <th className="px-4 py-3 font-medium text-right cursor-pointer hover:text-white transition-colors" onClick={() => handleSort('gross_profit')} data-id="element-338">
                  <div className="flex items-center justify-end gap-1" data-id="element-339">
                    Ganancia Bruta <ArrowUpDownIcon className="w-3 h-3" data-id="element-340" />
                  </div>
                </th>
                <th className="px-4 py-3 font-medium text-right cursor-pointer hover:text-white transition-colors" onClick={() => handleSort('gross_margin_pct')} data-id="element-341">
                  <div className="flex items-center justify-end gap-1" data-id="element-342">
                    Margen % <ArrowUpDownIcon className="w-3 h-3" data-id="element-343" />
                  </div>
                </th>
                <th className="px-4 py-3 font-medium text-right cursor-pointer hover:text-white transition-colors" onClick={() => handleSort('max_units_possible')} data-id="element-344">
                  <div className="flex items-center justify-end gap-1" data-id="element-345">
                    Unidades Posibles <ArrowUpDownIcon className="w-3 h-3" data-id="element-346" />
                  </div>
                </th>
                <th className="px-4 py-3 font-medium text-center" data-id="element-347">Estado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800" data-id="element-348">
              {loading ? <tr data-id="element-349">
                  <td colSpan={8} className="px-4 py-8 text-center" data-id="element-350">
                    <Loader2Icon className="w-6 h-6 text-dorado animate-spin mx-auto" data-id="element-351" />
                  </td>
                </tr> : filteredAndSortedItems.length === 0 ? <tr data-id="element-352">
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500" data-id="element-353">
                    No hay datos de rentabilidad disponibles.
                  </td>
                </tr> : filteredAndSortedItems.map((item, idx) => <tr key={idx} className="hover:bg-surface-light transition-colors" data-id="element-354">
                    <td className="px-4 py-3 font-medium text-white" data-id="element-355">
                      {item.item_name}
                    </td>
                    <td className="px-4 py-3 text-gray-400" data-id="element-356">{item.category}</td>
                    <td className="px-4 py-3 text-right text-gray-300" data-id="element-357">
                      ₡{item.sell_price.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right text-red-400" data-id="element-358">
                      ₡{item.recipe_cost.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-emerald-400" data-id="element-359">
                      ₡{item.gross_profit.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right text-emerald-400" data-id="element-360">
                      {item.gross_margin_pct.toFixed(1)}%
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-white" data-id="element-361">
                      {item.max_units_possible}
                    </td>
                    <td className="px-4 py-3 text-center" data-id="element-362">
                      {item.offer_status === 'STOP OFFERING' ? <span className="px-2 py-1 rounded-md text-xs font-medium bg-red-500/10 text-red-400 border border-red-500/20" data-id="element-363">
                          STOP
                        </span> : item.offer_status === 'LOW INVENTORY' ? <span className="px-2 py-1 rounded-md text-xs font-medium bg-dorado/10 text-dorado border border-dorado/20" data-id="element-364">
                          LOW
                        </span> : <span className="px-2 py-1 rounded-md text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" data-id="element-365">
                          OK
                        </span>}
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}